import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import Container from '../components/Container';
import useCurrentPage from '../hooks/use-set-current-page';
import Companies from '../components/Companies';
import { userListStatsAtom } from '../store/portfolio';
import { IListStatistics } from '../types/company';
import PageHeader from '../components/PageHeader';
import { getPercentDisplay } from '../utils/format-string';
import { getDowngradeBucket } from '../utils/downgrade-utilities';
import { headerInfoAtom } from '../store/header-info';

export default function List() {
  const currentPage = useCurrentPage();
  const { listId } = useParams<{ listId: string }>();

  const parsedListId = useMemo<number | null>(() => {
    if (!listId) return null;
    const n = Number(listId);
    return Number.isFinite(n) ? n : null;
  }, [listId]);

  const userListStats = useRecoilValue<IListStatistics[]>(userListStatsAtom);
  const [headerInfo, setHeaderInfo] = useRecoilState(headerInfoAtom);

  useEffect(() => {
    currentPage.setCurrentPage(`List - ${parsedListId ?? ''}`);
  }, [currentPage, parsedListId]);

  useEffect(() => {
    if (parsedListId === null) {
      setHeaderInfo(undefined);
      return;
    }

    const stat = userListStats.find((u) => u.listId === parsedListId);
    if (!stat) {
      setHeaderInfo(undefined);
      return;
    }

    setHeaderInfo({
      name: stat.listName,
      score: stat.current_score,
      change: getPercentDisplay(stat.previous_week_percent_change),
      signal: getDowngradeBucket(stat.current_score / 100.0),
      numCompanies: stat.numberOfCompanies,
    });
  }, [userListStats, parsedListId, setHeaderInfo]);

  useEffect(() => {
    // On unmount
    return () => {
      setHeaderInfo(undefined);
    };
  }, [setHeaderInfo]);

  if (parsedListId === null) {
    return (
      <div className="mb-auto bg-white lg:bg-ow-lighter-grey">
        <PageHeader headerInfo={undefined} isList={true} />
        <Container>
          <div className="lg:flex justify-left lg:w-full">
            <div className="lg:w-full lg:bg-white lg:p-8">
              <div className="text-sm text-ow-darker-grey">Invalid list.</div>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="mb-auto bg-white lg:bg-ow-lighter-grey">
      <PageHeader headerInfo={headerInfo} isList={true} />
      <Container>
        <div className="lg:flex justify-left lg:w-full">
          <div className="lg:w-full lg:bg-white lg:p-8">
            <Companies defaultList={[parsedListId]} />
          </div>
        </div>
      </Container>
    </div>
  );
}
